import isUndefined from 'lodash-es/isUndefined';
import { ContainmentError } from '../../containmentError';
import { BoundaryPoint, createRangeWithBoundaryPoints } from './boundaryPoint';
import { isText } from './node';
import {
  createPointAtEndOfNodeContents,
  createPointAtStartOfNodeContents,
  createPointWithEndOfRange,
  createPointWithStartOfRange,
  updateRangeWithPoints,
} from './point';

export interface TextRange {
  startIndex: number;
  endIndex: number;
}

export function clampRange(range: Range, container: Node): Range | null {
  const startOfContainerContents = createPointAtStartOfNodeContents(container);
  const endOfContainerContents = createPointAtEndOfNodeContents(container);

  let startPoint = createPointWithStartOfRange(range);
  let endPoint = createPointWithEndOfRange(range);

  if (startOfContainerContents.isAfterPoint(startPoint)) {
    if (startOfContainerContents.isAfterPoint(endPoint)) {
      return null;
    }

    startPoint = startOfContainerContents;
  }

  if (endPoint.isAfterPoint(endOfContainerContents)) {
    if (startPoint.isAfterPoint(endOfContainerContents)) {
      return null;
    }

    endPoint = endOfContainerContents;
  }

  const newRange = range.cloneRange();
  updateRangeWithPoints(newRange, startPoint, endPoint);

  // Assert we either have a null range or a range that is contained in the container.
  if (newRange && !container.contains(newRange.commonAncestorContainer)) {
    throw new ContainmentError();
  }

  return newRange;
}

export function createRangeWithTextRange(container: Node, { startIndex, endIndex }: TextRange): Range | null {
  const document = container.ownerDocument;
  if (!document) {
    return null;
  }

  let startPoint: BoundaryPoint | undefined;
  let endPoint: BoundaryPoint | undefined;

  if (startIndex === 0) {
    startPoint = { container, offset: 0 };
  }

  if (endIndex === 0) {
    endPoint = { container, offset: 0 };
  }

  const iterator = document.createNodeIterator(container, NodeFilter.SHOW_TEXT, {
    acceptNode: candidateNode =>
      isText(candidateNode) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });

  let nodeStartIndex = 0;
  let node: Text | null;
  while (!(startPoint && endPoint) && (node = iterator.nextNode() as Text | null)) {
    const nodeEndIndex = nodeStartIndex + node.data.length;

    if (nodeStartIndex <= startIndex && startIndex <= nodeEndIndex) {
      startPoint = { container: node, offset: startIndex - nodeStartIndex };
      endPoint = undefined;
    }

    if (nodeStartIndex <= endIndex && endIndex <= nodeEndIndex) {
      endPoint = { container: node, offset: endIndex - nodeStartIndex };
    }

    nodeStartIndex = nodeEndIndex;
  }

  if (!endPoint && endIndex === nodeStartIndex) {
    endPoint = { container, offset: container.childNodes.length };
  }

  return startPoint && endPoint ? createRangeWithBoundaryPoints(document, startPoint, endPoint) : null;
}

export function getTextContentInRange(range: Range): string {
  const { commonAncestorContainer, startContainer, startOffset, endContainer, endOffset } = range;
  const document = commonAncestorContainer.ownerDocument;
  if (!document) {
    return '';
  }

  let textContent = '';

  const walker = document.createTreeWalker(commonAncestorContainer);
  walker.currentNode = startContainer;
  do {
    if (isText(walker.currentNode)) {
      const textNode = walker.currentNode;

      if (textNode === startContainer) {
        if (textNode === endContainer) {
          textContent = textNode.data.substring(startOffset, endOffset);
        } else {
          textContent = textNode.data.substring(startOffset);
        }
      } else if (textNode === endContainer) {
        textContent += textNode.data.substring(0, endOffset);
      } else {
        textContent += textNode.data;
      }
    }

    if (walker.currentNode === endContainer) {
      break;
    }
  } while (walker.nextNode());

  return textContent;
}

export function createTextRangeWithRange(container: Node, range: Range | undefined): TextRange | undefined {
  const document = container.ownerDocument;
  if (!document || !range) {
    return undefined;
  }

  let startIndex;
  let endIndex;

  const firstNodeInRange =
    range.startOffset < range.startContainer.childNodes.length
      ? range.startContainer.childNodes[range.startOffset]
      : range.startContainer;
  const lastNodeInRange =
    range.endOffset > 0 && range.endOffset <= range.endContainer.childNodes.length
      ? range.endContainer.childNodes[range.endOffset - 1]
      : range.endContainer;

  const walker = document.createTreeWalker(container);
  let nodeStartIndex = 0;
  let nodeIsInRange = false;

  do {
    const node = walker.currentNode;
    const nodeLength = isText(node) ? node.data.length : 0;
    const nodeEndIndex = nodeStartIndex + nodeLength;

    if (node === firstNodeInRange) {
      nodeIsInRange = true;
    }

    if (nodeIsInRange && isUndefined(startIndex)) {
      if (isText(node) && node === range.startContainer) {
        startIndex = nodeStartIndex + range.startOffset;
      } else {
        startIndex = nodeStartIndex;
      }
    }

    if (nodeIsInRange) {
      if (isText(node) && node === range.endContainer) {
        endIndex = nodeStartIndex + range.endOffset;
      } else {
        endIndex = nodeEndIndex;
      }
    }

    if (node === lastNodeInRange) {
      break;
    }

    nodeStartIndex = nodeEndIndex;
  } while (walker.nextNode());

  if (isUndefined(startIndex) || isUndefined(endIndex)) {
    return undefined;
  }

  return { startIndex, endIndex };
}

export function createRangeWithNode(node: Node): Range | undefined {
  const document = node.ownerDocument;
  if (!document) {
    return undefined;
  }

  const range = document.createRange();
  range.selectNode(node);

  return range;
}

export function prettyPrintRange(range: Range) {
  const { startOffset, endOffset, startContainer, endContainer, collapsed, commonAncestorContainer } = range;

  return {
    startOffset,
    endOffset,
    startContainer,
    endContainer,
    collapsed,
    commonAncestorContainer,
  };
}
